import {
	type ClassValue,
	clsx,
} from 'clsx';
import {
	extendTailwindMerge,
	validators,
} from 'tailwind-merge';

const SEMANTIC_COLORS = [
	'brand-primary',
	'brand-primary-hover',
	'brand-primary-pressed',
	'brand-primary-disabled',
	'brand-primary-focus',
	'brand-primary-selected',
	'brand-primary-subtle',
	'brand-primary-muted',
	'brand-on-primary',
	'accent-default',
	'accent-subtle',
	'accent-strong',
	'text-default',
	'text-default-disabled',
	'text-default-selected',
	'text-muted',
	'text-subtle',
	'text-inverse',
	'text-on-brand',
	'text-link',
	'text-success',
	'text-danger',
	'text-warning',
	'surface-default',
	'surface-default-hover',
	'surface-default-pressed',
	'surface-default-selected',
	'surface-secondary',
	'surface-muted',
	'surface-subtle',
	'surface-strong',
	'surface-inverse',
	'surface-brand-subtle',
	'border-default',
	'border-default-hover',
	'border-default-focus',
	'border-subtle',
	'border-strong',
	'border-inverse',
	'border-brand',
	'feedback-success',
	'feedback-success-subtle',
	'feedback-success-muted',
	'feedback-success-strong',
	'feedback-danger',
	'feedback-danger-subtle',
	'feedback-danger-muted',
	'feedback-danger-strong',
	'feedback-warning',
	'feedback-warning-subtle',
	'feedback-warning-muted',
	'feedback-warning-strong',
	'feedback-info',
	'feedback-info-subtle',
	'feedback-info-muted',
	'feedback-info-strong',
	'icon-default',
	'icon-active',
	'icon-muted',
	'icon-subtle',
	'icon-inverse',
	'icon-on-brand',
	'icon-brand',
	'icon-success',
	'icon-danger',
	'icon-warning',
] as const;

const TEXT_COLOR_ALIASES = [
	'default',
	'default-disabled',
	'default-selected',
	'muted',
	'subtle',
	'inverse',
	'on-brand',
	'link',
	'success',
	'danger',
	'warning',
] as const;

const BORDER_COLOR_ALIASES = [
	'default',
	'default-hover',
	'default-focus',
	'subtle',
	'strong',
	'inverse',
	'brand',
] as const;

const RADIUS_VALUE_PARTS = [
	validators.isInteger,
	validators.isFraction,
	validators.isArbitraryValue,
	validators.isArbitraryVariable,
] as const;

const twMerge = extendTailwindMerge<
	| 'control-height'
	| 'control-min-height'
	| 'control-padding'
	| 'control-padding-x'
	| 'control-padding-y'
	| 'control-gap'
	| 'icon-size'
	| 'icon-hit-area'
	| 'icon-text-gap'
>({
	extend: {
		classGroups: {
			'font-size': [
				'text-heading-1',
				'text-heading-2',
				'text-heading-3',
				'text-body',
				'text-body-sm',
				'text-caption',
				'text-overline',
				/*
				 * Typography recipes (font-size + line-height + font-weight).
				 * Listed under font-size so they dedupe against each other and
				 * against raw text-* utilities. Mixing type-* with font-bold
				 * or leading-* is supported but does not dedupe — the last
				 * utility wins via CSS order.
				 */
				'type-title-1',
				'type-title-2',
				'type-heading',
				'type-subtitle-1',
				'type-h5',
				'type-h6',
				'type-subtitle',
				'type-subtitle-strong',
				'type-body-1',
				'type-body-1-strong',
				'type-body-2',
				'type-link',
				'type-button',
				'type-caption',
				'type-caption-2',
			],
			'font-weight': [
				'font-regular',
				'font-medium',
				'font-demi',
				'font-bold',
			],
			'font-family': [
				'font-heading',
				'font-body',
				'font-body-demi',
			],
			'text-color': [
				...TEXT_COLOR_ALIASES.map(c => `text-${c}`),
				...SEMANTIC_COLORS.map(c => `text-${c}`),
			],
			'bg-color': SEMANTIC_COLORS.map(c => `bg-${c}`),
			'border-color': [
				...BORDER_COLOR_ALIASES.map(c => `border-${c}`),
				...SEMANTIC_COLORS.map(c => `border-${c}`),
			],
			'ring-color': SEMANTIC_COLORS.map(c => `ring-${c}`),
			'outline-color': SEMANTIC_COLORS.map(c => `outline-${c}`),
			'divide-color': SEMANTIC_COLORS.map(c => `divide-${c}`),
			'fill': SEMANTIC_COLORS.map(c => `fill-${c}`),
			'stroke': SEMANTIC_COLORS.map(c => `stroke-${c}`),

			/*
			 * Semantic radius — dedupes against each other and the
			 * functional `rounded-N` integer utilities. `rounded-full`
			 * is part of this group (semantic role: pill / avatar) and
			 * dedupes against the others.
			 */
			'rounded': [
				{
					rounded: [
						'control',
						'surface',
						'overlay',
						'full',
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-s': [
				{
					'rounded-s': [
						'overlay',
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-e': [
				{
					'rounded-e': [
						'overlay',
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-t': [
				{
					'rounded-t': [
						'overlay',
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-b': [
				{
					'rounded-b': [
						'overlay',
						...RADIUS_VALUE_PARTS,
					],
				},
			],

			/* Semantic elevation. */
			'shadow': [
				'shadow-raised',
				'shadow-floating',
				'shadow-overlay',
				'shadow-inverse',
			],

			/* Semantic z-index layers. */
			'z': [
				'z-base',
				'z-raised',
				'z-sticky',
				'z-dropdown',
				'z-overlay',
				'z-modal-backdrop',
				'z-modal',
				'z-popover',
				'z-toast',
				'z-tooltip',
			],

			/* Semantic transition-duration. */
			'duration': [
				'duration-instant',
				'duration-fast',
				'duration-base',
				'duration-slow',
				'duration-slower',
			],

			/* Semantic transition-timing-function. */
			'ease': [
				'ease-standard',
				'ease-emphasized',
				'ease-decelerate',
				'ease-accelerate',
				'ease-linear',
			],

			/* Control sizing and spacing. */
			'control-height': [
				'h-control-sm',
				'h-control-md',
				'h-control-lg',
			],
			'control-min-height': [
				'min-h-control-sm',
				'min-h-control-md',
				'min-h-control-lg',
			],
			'control-padding': [
				'p-control-sm',
				'p-control-md',
				'p-control-lg',
			],
			'control-padding-x': ['px-control'],
			'control-padding-y': [
				'py-control-sm',
				'py-control-md',
				'py-control-lg',
			],
			'control-gap': ['gap-control'],

			/*
			 * Icon size — bundled width + height + min-* utilities. Dedupes
			 * against itself; not part of width/height groups so adjacent
			 * `w-*` / `h-*` utilities still apply if a component needs to
			 * override one axis.
			 */
			'icon-size': [
				'icon-size-xs',
				'icon-size-sm',
				'icon-size-md',
				'icon-size-lg',
				'icon-size-xl',
			],
			'icon-hit-area': [
				'icon-hit-area-min',
				'icon-hit-area-recommended',
			],
			'icon-text-gap': [
				'icon-text-gap',
				'icon-text-gap-lg',
			],
		},
	},
});

/**
 * Join conditional class values and dedupe conflicting Tailwind utilities,
 * including the semantic token utility groups defined for this design system.
 */
export default function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs));
}
